import { Download, Eye, ImageIcon, TrafficCone, Video } from 'lucide-react';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { AssetPreviewContent } from '@/components/asset-preview-content';
import { useDownload } from '@/hooks/useDownload';
import { isDefined } from '@/utilities/guards';
import { Badge } from '@/components/ui/badge';
import { FileType } from '@/types/fileType';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

const BROWSER_RENDERABLE_IMAGE_FORMATS = new Set(['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'svg']);

type AssetVariants = NonNullable<Asset['variants']>;
type DisplayVariant = NonNullable<AssetVariants[string]> & { slug: string };

// A variant whose only key is `url` is a placeholder the API returns before the file is generated
const isDisplayableVariant = (variant: AssetVariants[string]): variant is NonNullable<AssetVariants[string]> => {
  if (!isDefined(variant) || variant.internal) {
    return false;
  }
  const keys = Object.keys(variant);
  return !(keys.length === 1 && keys[0] === 'url');
};

const getDisplayVariants = (variants: Asset['variants']): Array<DisplayVariant> =>
  Object.entries(variants ?? {}).flatMap(([slug, variant]) =>
    isDisplayableVariant(variant) ? [{ ...variant, slug }] : [],
  );

const isBrowserPreviewable = (variant: DisplayVariant) => {
  const isImage = variant.type === FileType.Image || Boolean(variant.type?.startsWith('image/'));
  if (!isImage || !variant.format) {
    return true;
  }
  return BROWSER_RENDERABLE_IMAGE_FORMATS.has(variant.format);
};

const VariantRow = ({
  variant,
  asset,
  onDownload,
}: {
  variant: DisplayVariant;
  asset: Asset;
  onDownload: (url: string, fileName: string) => void;
}) => {
  const tCommon = useTranslations('common');
  const tVariants = useTranslations('variants');

  return (
    <div className="flex items-center gap-x-2 last:mb-0">
      <div>
        {variant.type === FileType.Image && <ImageIcon className="icon-6 text-ui-separator" />}
        {variant.type === FileType.Video && <Video className="icon-6 text-ui-separator" />}
      </div>

      <div className="flex grow flex-col">
        <div className="line-clamp-1 text-text-base">
          <Tooltip>
            <TooltipContent className="max-w-75 wrap-anywhere">
              <p>{variant.name}</p> ({variant.slug})
            </TooltipContent>
            <TooltipTrigger asChild>
              <div>
                <p className="inline font-bold wrap-anywhere">{variant.name}</p> ({variant.slug})
              </div>
            </TooltipTrigger>
          </Tooltip>
        </div>
        <div className="pointer-events-none flex flex-wrap items-center gap-1 overflow-hidden">
          {variant.automatic && <Badge variant="bevel">{tVariants('auto')}</Badge>}
          {variant.public && <Badge variant="highlight">{tVariants('public')}</Badge>}
          {variant.format && <Badge variant="base">{variant.format.toUpperCase()}</Badge>}
          {isDefined(variant.width) && isDefined(variant.height) && (
            <Badge variant="bevel">{tVariants('dimensions', { width: variant.width, height: variant.height })}</Badge>
          )}
          {variant.watermark && <Badge variant="warning">{tVariants('watermarked')}</Badge>}
        </div>
      </div>

      <div className="flex shrink-0 gap-x-1">
        <Dialog>
          <DialogTrigger asChild>
            <OptionAction
              icon={Eye}
              disabled={!isBrowserPreviewable(variant)}
              data-cy={`option-action-button-variant-preview-${variant.slug}`}
              tooltip={tCommon('preview')}
            />
          </DialogTrigger>
          <DialogContent className="sm:max-w-(--breakpoint-lg)">
            <AssetPreviewContent
              name={variant.name!}
              type={variant.type}
              thumbnail={variant.url ?? undefined}
              asset={asset}
              variantKey={variant.slug}
              renderVideoPlayer={variant.type === FileType.Video || variant.type === 'video/mp4'}
            />
          </DialogContent>
        </Dialog>
        <OptionAction
          disabled={!variant.name || !variant.format}
          icon={Download}
          onClick={() => variant.url && onDownload(variant.url, `${variant.name}.${variant.format}`)}
          data-cy={`option-action-button-variant-download-${variant.slug}`}
          tooltip={tCommon('download')}
        />
      </div>
    </div>
  );
};

export const VariantsAccordion = ({ asset, disabled }: { asset: Asset; disabled: boolean }) => {
  const tDownloads = useTranslations('downloads');
  const tVariants = useTranslations('variants');

  const download = useDownload({
    title: tDownloads('variantDownloadingTitle'),
    description: tDownloads('variantDownloadStartedDescription'),
  });

  const displayVariants = getDisplayVariants(asset?.variants);

  if (displayVariants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-input-background py-8 text-center text-text-gray-alternate">
        <div className="flex flex-col">
          <TrafficCone className="mx-auto mb-2" size={28} strokeWidth={1.5} />
          {tVariants('none', { count: 0 })}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full scrollbar-none space-y-6 overflow-y-auto bg-input-background p-3 text-text-gray-alternate">
      {!disabled &&
        displayVariants.map((variant) => (
          <VariantRow key={variant.slug} variant={variant} asset={asset} onDownload={download} />
        ))}
    </div>
  );
};
